import { useCallback } from "react";

import { FrontendRoutes, DashboardRoutes, UrlBuilder, BuiltRoute } from "@simplyk/common";
import { usePathname, useSearchParams } from "next/navigation";

type Route = FrontendRoutes | DashboardRoutes | BuiltRoute;
type SearchParamsType = Record<string, string>;
type SearchParamsCallbackType = (searchParams: SearchParamsType) => SearchParamsType;

export const appendSearchParams = (searchParamsToAppend: SearchParamsType) => (searchParams: SearchParamsType) => {
  return {
    ...searchParams,
    ...searchParamsToAppend,
  } as SearchParamsType;
};

export const removeSearchParams =
  (...searchParamsToRemove: string[]) =>
  (searchParams: SearchParamsType) => {
    const newSearchParams = { ...searchParams };
    searchParamsToRemove.forEach((param) => {
      newSearchParams[param] = "";
    });
    return newSearchParams;
  };

export const useUrlBuilder = () => {
  const routerPathname = usePathname();
  const routerSearchParams = useSearchParams();

  return useCallback(
    ({
      route,
      hash,
      searchParams,
    }: {
      route?: Route;
      hash?: string;
      searchParams?: SearchParamsType | SearchParamsCallbackType;
    }) => {
      // When running in Storybook, routerSearchParams is undefined, treat as empty object
      const routerSearchParamsEntries = routerSearchParams ? Object.fromEntries(routerSearchParams) : {};

      const searchParamsValue = Object.fromEntries(
        Object.entries(
          typeof searchParams === "function"
            ? searchParams(routerSearchParamsEntries)
            : (searchParams ?? routerSearchParamsEntries)
        ).filter(([, value]) => !!value)
      );
      const finalSearchParams = new URLSearchParams(searchParamsValue);
      const pathname = (route instanceof BuiltRoute ? route.path : route) ?? routerPathname;

      return UrlBuilder({
        pathname: pathname.toString(),
        hash,
        searchParams: finalSearchParams,
      }).build();
    },
    [routerPathname, routerSearchParams]
  );
};
